import { ChunkedMapData, ServerMessagePayload } from '@common/api';

import { ClientGlobalSystem } from './ClientBase';

/**
 * System that updates the camera position to follow the target entity.
 * Reads the CameraFollow component to determine the target, then updates
 * the camera's Transform and applies the transform to the world container.
 *
 * The camera is clamped to map boundaries to prevent showing blank space
 * beyond map edges. When the player approaches a boundary, they move towards
 * the edge of the viewport rather than staying centred.
 */
export class CameraFollowSystem extends ClientGlobalSystem {
  run(): ServerMessagePayload[] {
    const cameraService = this.clientServices.camera;
    const cameraEntityId = cameraService.cameraEntityId;

    if (cameraEntityId === undefined) {
      return [];
    }

    const world = this.getWorld();
    const cameraFollow = world.getComponent(cameraEntityId, 'CameraFollow');

    if (!cameraFollow) {
      return [];
    }

    // Get target position (prefer VisualPosition for smooth rendering)
    const targetPos =
      world.getComponent(cameraFollow.targetEntityId, 'VisualPosition') ||
      world.getComponent(cameraFollow.targetEntityId, 'Transform');

    if (!targetPos) {
      return [];
    }

    // Get scene metadata for boundary clamping
    const targetTransform = world.getComponent(cameraFollow.targetEntityId, 'Transform');
    const sceneMetadata = targetTransform
      ? this.clientServices.chunkData.getSceneMetadata(targetTransform.sceneId)
      : undefined;

    // Calculate clamped camera position
    if (sceneMetadata) {
      cameraService.updateZoom(sceneMetadata.chunkSize, sceneMetadata.tileWidth);
    }

    const clampedPos = this.clampCameraPosition(
      targetPos.x,
      targetPos.y,
      sceneMetadata,
      cameraService.screenWidth,
      cameraService.screenHeight,
      cameraService.zoom,
    );

    // Update camera Transform to clamped position
    const cameraTransform = world.getComponent(cameraEntityId, 'Transform');
    if (cameraTransform) {
      cameraTransform.x = clampedPos.x;
      cameraTransform.y = clampedPos.y;
    }

    // Apply to world container
    const stage = this.clientServices.render.stage;
    if (stage) {
      cameraService.applyToContainer(stage, clampedPos.x, clampedPos.y);
    }

    return [];
  }

  /**
   * Clamp camera position to keep viewport within map boundaries.
   * If the map is smaller than the viewport, centre the map instead.
   */
  private clampCameraPosition(
    targetX: number,
    targetY: number,
    metadata: ChunkedMapData | undefined,
    screenWidth: number,
    screenHeight: number,
    zoom: number,
  ): { x: number; y: number } {
    // No metadata = no clamping (fallback to unclamped position)
    if (!metadata) {
      return { x: targetX, y: targetY };
    }

    // Calculate world-space map bounds
    const mapMinX = metadata.minChunkX * metadata.chunkSize * metadata.tileWidth;
    const mapMaxX = (metadata.maxChunkX + 1) * metadata.chunkSize * metadata.tileWidth;
    const mapMinY = metadata.minChunkY * metadata.chunkSize * metadata.tileHeight;
    const mapMaxY = (metadata.maxChunkY + 1) * metadata.chunkSize * metadata.tileHeight;

    // Calculate viewport dimensions in world space
    const viewWidth = screenWidth / zoom;
    const viewHeight = screenHeight / zoom;
    const halfViewWidth = viewWidth / 2;
    const halfViewHeight = viewHeight / 2;

    // Calculate map dimensions
    const mapWidth = mapMaxX - mapMinX;
    const mapHeight = mapMaxY - mapMinY;

    // Clamp or centre depending on whether map is larger than viewport
    let clampedX: number;
    let clampedY: number;

    if (mapWidth <= viewWidth) {
      // Map is narrower than viewport: centre horizontally
      clampedX = (mapMinX + mapMaxX) / 2;
    } else {
      // Clamp so viewport stays within map bounds
      clampedX = Math.max(mapMinX + halfViewWidth, Math.min(targetX, mapMaxX - halfViewWidth));
    }

    if (mapHeight <= viewHeight) {
      // Map is shorter than viewport: centre vertically
      clampedY = (mapMinY + mapMaxY) / 2;
    } else {
      // Clamp so viewport stays within map bounds
      clampedY = Math.max(mapMinY + halfViewHeight, Math.min(targetY, mapMaxY - halfViewHeight));
    }

    return { x: clampedX, y: clampedY };
  }
}
